import React, { useState, useEffect } from 'react';
import { useLeads, useLeadsLoad } from '../../services/leads';
import { isBooked, stageOf } from './stages';
import RouteMap from './RouteMap';
import './RoutesDashboard.css';
import AgentPicker from '../../components/AgentPicker';
import { loadAgents } from '../../services/customers';
import { CURRENT_USER } from '../../services/currentUser';

const localISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const TODAY = localISO(new Date());

// open on today — or, if today is empty, the next day that has appointments
const firstDayWithWork = (leads) => {
  const days = leads.map((l) => l.appointmentStart.slice(0, 10)).filter((d) => d >= TODAY).sort();
  return days.includes(TODAY) || !days.length ? TODAY : days[0];
};

const RoutesDashboard = ({ onSelectInspection }) => {
  const allLeads = useLeads(); // live — status changes and reschedules show immediately
  const load = useLeadsLoad();
  const [agent, setAgent] = useState('All');
  const [assigning, setAssigning] = useState(null); // admin: the visit whose agent is being picked
  // every active agent in Zoho (even one with no visits yet) + anyone on the route
  const [zohoAgents, setZohoAgents] = useState([]);
  useEffect(() => {
    loadAgents().then((list) => setZohoAgents(list.map((a) => a.name))).catch(() => {});
  }, []);
  const agents = [...new Set([...zohoAgents, ...allLeads.map((l) => l.agentName).filter(Boolean)])].sort();
  const leads = agent === 'All' ? allLeads : allLeads.filter((l) => l.agentName === agent);
  const [selectedDate, setSelectedDate] = useState(() => firstDayWithWork(leads));
  const [dateRangeStart, setDateRangeStart] = useState(() => new Date(`${firstDayWithWork(leads)}T12:00:00`));
  const [appointments, setAppointments] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [view, setView] = useState('list'); // 'list' or 'map'

  // first load from Zoho: if today is empty, open on the next day that has appointments
  const [jumped, setJumped] = useState(load.status === 'ready');
  useEffect(() => {
    if (jumped || load.status !== 'ready') return;
    const day = firstDayWithWork(allLeads);
    setSelectedDate(day);
    setDateRangeStart(new Date(`${day}T12:00:00`));
    setJumped(true);
  }, [jumped, load.status, allLeads]);

  useEffect(() => {
    // Filter appointments by selected date
    const byTime = (a, b) => a.appointmentStart.localeCompare(b.appointmentStart);
    const forDay = leads
      .filter(inspection => inspection.appointmentStart.startsWith(selectedDate))
      .sort(byTime);
    // booked stops first (in time order), everything else at the bottom
    setAppointments([...forDay.filter(isBooked), ...forDay.filter((a) => !isBooked(a))]);
    setSelectedAppointment(null);
  }, [selectedDate, allLeads, agent]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAppointmentClick = (appointment) => {
    setSelectedAppointment(appointment);
    if (onSelectInspection) {
      onSelectInspection(appointment);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const getDayOfWeek = (dateString) => {
    // noon local — "2026-10-13" alone is midnight UTC, which is still Oct 12 in Toronto
    const date = new Date(`${dateString}T12:00:00`);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatDateForButton = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDateISO = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getVisibleDates = () => {
    const dates = [];
    const start = new Date(dateRangeStart);
    for (let i = 0; i < 7; i++) {
      dates.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
    }
    return dates;
  };

  const handlePrevWeek = () => {
    const newStart = new Date(dateRangeStart);
    newStart.setDate(newStart.getDate() - 7);
    setDateRangeStart(newStart);
  };

  const handleNextWeek = () => {
    const newStart = new Date(dateRangeStart);
    newStart.setDate(newStart.getDate() + 7);
    setDateRangeStart(newStart);
  };

  return (
    <div className="routes-dashboard">
      {/* HEADER */}
      <div className="routes-header">
        <h1>Route Dashboard</h1>
        <p>
          {getDayOfWeek(selectedDate)} · {appointments.filter(isBooked).length} stop{appointments.filter(isBooked).length === 1 ? '' : 's'}
          {appointments.some((a) => !isBooked(a)) && ` · ${appointments.filter((a) => !isBooked(a)).length} not booked`}
        </p>
      </div>

      {/* AGENT FILTER — everyone shares one login for now */}
      {agents.length > 1 && (
        <div className="routes-agents" role="tablist" aria-label="Agent">
          {['All', ...agents].map((a) => (
            <button key={a} type="button" role="tab" aria-selected={agent === a} className={`routes-agent${agent === a ? ' active' : ''}`} onClick={() => setAgent(a)}>
              {a}
            </button>
          ))}
        </div>
      )}

      {/* DATE SELECTOR */}
      <div className="date-selector-container">
        <button className="date-nav-btn" onClick={handlePrevWeek}>← Prev</button>
        <div className="date-selector">
          {getVisibleDates().map((date) => {
            const dateISO = formatDateISO(date);
            const isToday = dateISO === TODAY;
            const isSelected = dateISO === selectedDate;
            return (
              <button
                key={dateISO}
                onClick={() => setSelectedDate(dateISO)}
                className={`date-btn ${isSelected ? 'active' : ''} ${isToday ? 'today' : ''}`}
              >
                {formatDateForButton(date)}
              </button>
            );
          })}
        </div>
        <button className="date-nav-btn" onClick={handleNextWeek}>Next →</button>
      </div>

      {/* VIEW TOGGLE */}
      <div className="view-toggle">
        <button
          className={`toggle-btn ${view === 'list' ? 'active' : ''}`}
          onClick={() => setView('list')}
        >
          📋 List
        </button>
        <button
          className={`toggle-btn ${view === 'map' ? 'active' : ''}`}
          onClick={() => setView('map')}
        >
          🗺️ Map
        </button>
      </div>

      {/* MAIN CONTENT */}
      <div className="routes-content">
        {/* APPOINTMENTS LIST */}
        {view === 'list' && (
          <div className="appointments-list">
            {appointments.map((apt, idx) => (
              <div
                key={apt.id}
                className={`appointment-card ${selectedAppointment?.id === apt.id ? 'selected' : ''}`}
                onClick={() => handleAppointmentClick(apt)}
              >
                <div className="stop-number">
                  {/* only booked inspections are numbered stops — they're always first, so idx + 1 is the stop */}
                  <span className={`badge${isBooked(apt) ? '' : ' off'}`}>{isBooked(apt) ? idx + 1 : '–'}</span>
                </div>

                <div className="appointment-content">
                  <div className="appointment-header">
                    <h3>{apt.contactName}</h3>
                    <span className="time">
                      {formatTime(apt.appointmentStart)} - {formatTime(apt.appointmentEnd)}
                    </span>
                  </div>

                  <div className="appointment-details">
                    <p className="address">📍 {apt.serviceAddress}</p>
                    {CURRENT_USER.admin ? (
                      // admin: tap to assign / change the agent (doesn't open the appointment)
                      <button
                        type="button"
                        className={`agent agent-pick${apt.agentName ? '' : ' unassigned'}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setAssigning(apt);
                        }}
                      >
                        👤 {apt.agentName || 'Not assigned'} <span aria-hidden="true">✎</span>
                      </button>
                    ) : apt.agentName ? (
                      <p className="agent">👤 {apt.agentName}</p>
                    ) : (
                      <p className="agent unassigned">👤 Not assigned</p>
                    )}
                    {Number(apt.distanceFromDepot) > 0 && <p className="distance">📏 {apt.distanceFromDepot} mi</p>}
                    {apt.bookingNotes && <p className="has-notes">📝 Booking notes</p>}
                  </div>

                  <div className="appointment-footer">
                    <span className={`confirmation ${stageOf(apt).tone}`}>{stageOf(apt).label}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {assigning && <AgentPicker lead={assigning} onClose={() => setAssigning(null)} />}

        {/* MAP VIEW */}
        {view === 'map' && (
          <div className="map-view active">
            <RouteMap appointments={appointments} onOpen={handleAppointmentClick} />
          </div>
        )}
      </div>
    </div>
  );
};

export default RoutesDashboard;
